import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 1 | 2 | 3 | 4;
type Step = number;
export function PaidReportInputs() {
  const navigate = useNavigate();
  const [showEntry, setShowEntry] = useState(true);
  const [currentPhase, setCurrentPhase] = useState<Phase>(1);
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [formData, setFormData] = useState({
    // Phase 1: Entry Pressure
    entryReason: '',
    // Phase 2: Core Loop
    trigger: '',
    dominantResponse: [] as string[],
    typicalOutcome: '',
    // Phase 3: Generalization
    whereElse: '',
    patternDuration: '',
    // Phase 4: Current Strain
    innerTension: '',
    hardest: ''
  });
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleMultiSelect = (field: string, value: string) => {
    setFormData(prev => {
      const current = prev[field as keyof typeof prev] as string[];
      if (current.includes(value)) {
        return {
          ...prev,
          [field]: current.filter(v => v !== value)
        };
      } else if (current.length < 2) {
        return {
          ...prev,
          [field]: [...current, value]
        };
      }
      return prev;
    });
  };
  const handleStartDiagnostic = () => {
    setShowEntry(false);
  };
  const handleNext = () => {
    // Phase 1 navigation (Entry Pressure - 1 question)
    if (currentPhase === 1) {
      if (currentStep === 1 && !formData.entryReason) return;
      setCurrentPhase(2);
      setCurrentStep(1);
    }
    // Phase 2 navigation (Core Loop - 3 questions)
    else if (currentPhase === 2) {
      if (currentStep === 1 && !formData.trigger) return;
      if (currentStep === 2 && formData.dominantResponse.length === 0) return;
      if (currentStep === 3 && !formData.typicalOutcome) return;
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase(3);
        setCurrentStep(1);
      }
    }
    // Phase 3 navigation (Generalization - 2 questions)
    else if (currentPhase === 3) {
      if (currentStep === 1 && !formData.whereElse) return;
      if (currentStep === 2 && !formData.patternDuration) return;
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase(4);
        setCurrentStep(1);
      }
    }
    // Phase 4 navigation (Current Strain - 2 questions)
    else if (currentPhase === 4) {
      if (currentStep === 1 && !formData.innerTension) return;
      if (currentStep === 2 && !formData.hardest) return;
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowEndScreen(true);
      }
    }
  };
  const handleGenerateReport = () => {
    setIsGenerating(true);
    console.log('Paid report inputs:', formData);
    // Simulate report generation
    setTimeout(() => {
      navigate('/paid-report-output');
    }, 3000);
  };
  // Phase 1 options - Entry Pressure
  const entryReasonOptions = ['A pattern keeps repeating', 'I feel blocked or stuck', "I don't trust my decisions", 'I feel conflicted internally', 'I want clarity before acting'];
  // Phase 2 options - Core Loop
  const triggerOptions = ['Specific person', 'Type of situation', 'Time pressure', 'Uncertainty', 'Responsibility'];
  const responseOptions = ['Freeze or delay', 'Over-analyze', 'Over-perform', 'Withdraw', 'Push harder', 'Justify or explain'];
  const outcomeOptions = ['Temporary relief', 'Increased tension', 'Pattern repeats', 'Exhaustion', 'Avoidance'];
  // Phase 3 options - Generalization
  const whereElseOptions = ['Work', 'Relationships', 'Family', 'Personal decisions', 'Multiple contexts'];
  const durationOptions = ['Recent (weeks)', 'Ongoing (months)', 'Long-term (years)', 'Recurring pattern'];
  // Phase 4 options - Current Strain
  const tensionOptions = ['Guilt', 'Resentment', 'Fear', 'Inadequacy', 'Frustration'];
  const hardestOptions = ['Admitting the pattern', 'Changing behavior', 'Letting go of control', 'Trusting the process', 'Facing consequences'];
  if (showEntry) {
    return <>
        <Helmet data-id="element-1971">
          <title data-id="element-1972">Diagnostic Input — Decode | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-1973" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-1974">
          <header data-id="element-1975">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-1976">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1977">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-1978">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1979" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-1980">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-1981">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-1982">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-1983">
                Diagnostic input
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed max-w-xl mx-auto" data-id="element-1984">
                Answering these helps generate an accurate report.
                <br data-id="element-1985" />
                There are no right or wrong responses.
              </p>
              <button onClick={handleStartDiagnostic} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-1986">
                Start Diagnostic
              </button>
            </div>
          </main>
        </div>
      </>;
  }
  if (isGenerating) {
    return <>
        <Helmet data-id="element-1987">
          <title data-id="element-1988">Generating Report — Decode | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-1989" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-1990">
          <div className="text-center" data-id="element-1991">
            <p className="text-xl text-[#F5F5F5]/70" data-id="element-1992">Analyzing patterns...</p>
          </div>
        </div>
      </>;
  }
  if (showEndScreen) {
    return <>
        <Helmet data-id="element-1993">
          <title data-id="element-1994">Ready to Generate — Decode | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-1995" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-1996">
          <header data-id="element-1997">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-1998">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1999">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2000">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2001" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-2002">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-2003">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-2004">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-2005">
                Ready to generate your report
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed" data-id="element-2006">
                Context, response patterns, and constraints have been captured.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2007">
                <button onClick={handleGenerateReport} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2008">
                  Generate Full Report
                </button>
                <button onClick={() => {
                setShowEndScreen(false);
                setCurrentPhase(1);
                setCurrentStep(1);
              }} className="px-8 py-5 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-2009">
                  Edit responses
                </button>
              </div>
            </div>
          </main>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-2010">
        <title data-id="element-2011">Diagnostic Input — Decode | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-2012" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-2013">
        <header data-id="element-2014">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2015">
            <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-2016">
              <p className="text-xs text-[#F5F5F5]/50 text-center tracking-wider" data-id="element-2017">
                Diagnostic progress — Phase {currentPhase} of 4
              </p>
            </div>
          </nav>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-2018">
          <div className="max-w-2xl mx-auto w-full" data-id="element-2019">
            {/* Phase 1: Entry Pressure */}
            {currentPhase === 1 && currentStep === 1 && <div data-id="element-2020">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2021">
                  Why are you doing this decode now?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2022">
                  {entryReasonOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.entryReason === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2023">
                      <input type="radio" name="entryReason" value={option} checked={formData.entryReason === option} onChange={e => handleOptionSelect('entryReason', e.target.value)} className="sr-only" data-id="element-2024" />
                      <span className="text-base" data-id="element-2025">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.entryReason} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2026">
                  Continue
                </button>
              </div>}

            {/* Phase 2: Core Loop */}
            {currentPhase === 2 && currentStep === 1 && <div data-id="element-2027">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2028">
                  What typically triggers this pattern?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2029">
                  {triggerOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.trigger === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2030">
                      <input type="radio" name="trigger" value={option} checked={formData.trigger === option} onChange={e => handleOptionSelect('trigger', e.target.value)} className="sr-only" data-id="element-2031" />
                      <span className="text-base" data-id="element-2032">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.trigger} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2033">
                  Continue
                </button>
              </div>}

            {currentPhase === 2 && currentStep === 2 && <div data-id="element-2034">
                <h1 className="text-3xl md:text-4xl font-medium mb-6 text-center leading-tight tracking-tight" data-id="element-2035">
                  What do you typically do when this happens?
                </h1>
                <p className="text-sm text-[#F5F5F5]/50 text-center mb-12" data-id="element-2036">
                  Select up to 2 responses
                </p>
                <div className="space-y-4 mb-12" data-id="element-2037">
                  {responseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.dominantResponse.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2038">
                      <input type="checkbox" name="dominantResponse" value={option} checked={formData.dominantResponse.includes(option)} onChange={e => handleMultiSelect('dominantResponse', e.target.value)} className="sr-only" data-id="element-2039" />
                      <span className="text-base" data-id="element-2040">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={formData.dominantResponse.length === 0} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2041">
                  Continue
                </button>
              </div>}

            {currentPhase === 2 && currentStep === 3 && <div data-id="element-2042">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2043">
                  What typically happens after?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2044">
                  {outcomeOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.typicalOutcome === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2045">
                      <input type="radio" name="typicalOutcome" value={option} checked={formData.typicalOutcome === option} onChange={e => handleOptionSelect('typicalOutcome', e.target.value)} className="sr-only" data-id="element-2046" />
                      <span className="text-base" data-id="element-2047">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.typicalOutcome} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2048">
                  Continue
                </button>
              </div>}

            {/* Phase 3: Generalization */}
            {currentPhase === 3 && currentStep === 1 && <div data-id="element-2049">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2050">
                  Where else does this pattern appear?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2051">
                  {whereElseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.whereElse === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2052">
                      <input type="radio" name="whereElse" value={option} checked={formData.whereElse === option} onChange={e => handleOptionSelect('whereElse', e.target.value)} className="sr-only" data-id="element-2053" />
                      <span className="text-base" data-id="element-2054">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.whereElse} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2055">
                  Continue
                </button>
              </div>}

            {currentPhase === 3 && currentStep === 2 && <div data-id="element-2056">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2057">
                  How long has this pattern been active?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2058">
                  {durationOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.patternDuration === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2059">
                      <input type="radio" name="patternDuration" value={option} checked={formData.patternDuration === option} onChange={e => handleOptionSelect('patternDuration', e.target.value)} className="sr-only" data-id="element-2060" />
                      <span className="text-base" data-id="element-2061">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.patternDuration} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2062">
                  Continue
                </button>
              </div>}

            {/* Phase 4: Current Strain */}
            {currentPhase === 4 && currentStep === 1 && <div data-id="element-2063">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2064">
                  What underlying pressure feels most active?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2065">
                  {tensionOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.innerTension === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2066">
                      <input type="radio" name="innerTension" value={option} checked={formData.innerTension === option} onChange={e => handleOptionSelect('innerTension', e.target.value)} className="sr-only" data-id="element-2067" />
                      <span className="text-base" data-id="element-2068">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.innerTension} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2069">
                  Continue
                </button>
              </div>}

            {currentPhase === 4 && currentStep === 2 && <div data-id="element-2070">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-2071">
                  What feels hardest about this pattern right now?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-2072">
                  {hardestOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.hardest === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2073">
                      <input type="radio" name="hardest" value={option} checked={formData.hardest === option} onChange={e => handleOptionSelect('hardest', e.target.value)} className="sr-only" data-id="element-2074" />
                      <span className="text-base" data-id="element-2075">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.hardest} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2076">
                  Continue
                </button>
              </div>}
          </div>
        </main>
      </div>
    </>;
}